import { FC, useState } from 'react';
import { trpc } from 'src/trpc';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { useToast } from 'components/useToast';
import { MainLayout } from '../MainLayout';
import { Loader } from 'src/components/Loader';
import { DataTable } from 'src/components/table/DataTable';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';

interface FeatureFlag {
  name: string;
  enabled: boolean;
}

export const FeatureFlagsDashboard: FC = () => {
  const [flagToDelete, setFlagToDelete] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const { successToast, errorToast } = useToast();

  // Fetch feature flags
  const { data, isLoading, isError, error, refetch } =
    trpc.admin.featureFlags.listFeatureFlags.useQuery();
  const utils = trpc.useUtils();

  // Toggle feature flag mutation
  const toggleFeatureMutation = trpc.admin.featureFlags.updateFeatureFlag.useMutation({
    onSuccess: () => {
      successToast({ title: 'Feature flag updated successfully' });
      utils.admin.featureFlags.listFeatureFlags.invalidate();
      refetch();
    },
    onError: (error) => {
      errorToast({ title: 'Error updating feature flag', description: error.message });
    },
  });

  // Delete feature flag mutation
  const deleteFeatureMutation = trpc.admin.featureFlags.deleteFeatureFlag.useMutation({
    onSuccess: () => {
      successToast({ title: 'Feature flag deleted successfully' });
      refetch();
      setIsDeleteDialogOpen(false);
    },
    onError: (error) => {
      errorToast({ title: 'Error deleting feature flag', description: error.message });
      setIsDeleteDialogOpen(false);
    },
  });

  const handleToggle = (name: string, currentState: boolean) => {
    toggleFeatureMutation.mutate({
      feature: name,
      enabled: !currentState,
    });
  };

  const handleDeleteClick = (name: string) => {
    setFlagToDelete(name);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (flagToDelete) {
      deleteFeatureMutation.mutate({ name: flagToDelete });
    }
  };

  // Define columns for DataTable
  const columns: ColumnDef<FeatureFlag>[] = [
    {
      accessorKey: 'name',
      header: 'Feature Name',
    },
    {
      accessorKey: 'enabled',
      header: 'Status',
      cell: ({ row }) => (
        <div className="flex items-center">
          <Switch
            checked={row.original.enabled}
            onCheckedChange={() => handleToggle(row.original.name, row.original.enabled)}
            disabled={toggleFeatureMutation.isPending}
          />
          <span className="ml-2">{row.original.enabled ? 'Enabled' : 'Disabled'}</span>
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <Button
          variant="destructive"
          size="sm"
          onClick={() => handleDeleteClick(row.original.name)}
          disabled={deleteFeatureMutation.isPending}
        >
          Delete
        </Button>
      ),
    },
  ];

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex justify-center items-center h-96">
          <Loader />
        </div>
      </MainLayout>
    );
  }

  if (isError) {
    return (
      <MainLayout>
        <div className="p-5 text-center">
          <div className="text-red-500">Error loading feature flags: {error?.message}</div>
          <Button variant="default" className="mt-4" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </MainLayout>
    );
  }

  const featureFlags = data?.featureFlags || [];

  return (
    <MainLayout>
      <div className="container mx-auto py-6">
        <h1 className="text-2xl font-bold mb-6">Feature Flags</h1>

        {featureFlags.length === 0 ? (
          <p>No feature flags available.</p>
        ) : (
          <div className="flex flex-col h-full">
            <DataTable
              columns={columns}
              data={featureFlags}
              currentPage={1}
              sorting={sorting}
              onSortingChange={setSorting}
              hasNextPage={false}
              hasPreviousPage={false}
              onPageChange={() => {}}
              pageCount={1}
              total={featureFlags.length}
              resourceName="feature flags"
              offset={0}
              limit={25}
            />
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Feature Flag</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete the "{flagToDelete}" feature flag? This action
                cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setIsDeleteDialogOpen(false)}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} disabled={deleteFeatureMutation.isPending}>
                {deleteFeatureMutation.isPending ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MainLayout>
  );
};
